// Portfolio Administration v1.0 PR 3 §7.3 — Add Party modal.
//
// Counterparty selection is the unified typeahead picker (type-scoped
// search over each module's list endpoint); types without a search seam
// (LocalEnforcer / OtherContact) keep the manual id input.

import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button, Input, Label, Textarea } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useAddLoanParty } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import {
  CounterpartyPicker,
  hasCounterpartySearch,
  type SelectedCounterparty,
} from "./CounterpartyPicker";

interface AddPartyModalProps {
  loanId: string;
  open: boolean;
  onClose: () => void;
  /** Hint for the default counterparty type for the section the modal
   *  was launched from (e.g. "Person" for Guarantors). */
  defaultCounterpartyType?: string;
  /** Default role-on-loan string (e.g. "Guarantor"). */
  defaultRoleOnLoan?: string;
}

const COUNTERPARTY_TYPES = [
  "Business",
  "Person",
  "LegalEntity",
  "Broker",
  "Vendor",
  "LocalEnforcer",
  "PhoenixUser",
  "OtherContact",
];

export function AddPartyModal({
  loanId,
  open,
  onClose,
  defaultCounterpartyType = "Person",
  defaultRoleOnLoan = "",
}: AddPartyModalProps) {
  const toast = useToast();
  const add = useAddLoanParty(loanId);

  const [counterpartyType, setCounterpartyType] = useState(defaultCounterpartyType);
  // Picker selection (searchable types) / manual id (fallback types).
  const [selected, setSelected] = useState<SelectedCounterparty | null>(null);
  const [manualId, setManualId] = useState("");
  const [roleOnLoan, setRoleOnLoan] = useState(defaultRoleOnLoan);
  const [notes, setNotes] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [isKeyPerson, setIsKeyPerson] = useState(false);
  const [isPrimaryContact, setIsPrimaryContact] = useState(false);

  const searchable = hasCounterpartySearch(counterpartyType);
  const counterpartyId = searchable ? (selected?.id ?? "") : manualId;

  const reset = () => {
    setCounterpartyType(defaultCounterpartyType);
    setSelected(null);
    setManualId("");
    setRoleOnLoan(defaultRoleOnLoan);
    setNotes("");
    setEffectiveFrom("");
    setIsKeyPerson(false);
    setIsPrimaryContact(false);
  };

  const onSubmit = async () => {
    if (!counterpartyId.trim() || !roleOnLoan.trim()) {
      toast.error(
        searchable
          ? "Pick a counterparty and enter the role on loan."
          : "Counterparty ID and role on loan are required.",
      );
      return;
    }
    try {
      await add.mutateAsync({
        counterpartyType,
        counterpartyId: counterpartyId.trim(),
        roleOnLoan: roleOnLoan.trim(),
        notes: notes || null,
        effectiveFrom: effectiveFrom || null,
        isKeyPerson,
        isPrimaryContact,
      });
      toast.success("Party added.");
      reset();
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Could not add party.",
      );
    }
  };

  return (
    <Modal open={open} onClose={onClose} dismissOnBackdropClick={false} title="Add party to loan" size="md">
      <div className="flex flex-col gap-space-3" data-testid="add-party-modal-body">
        <div>
          <Label htmlFor="counterparty-type">Counterparty type</Label>
          <select
            id="counterparty-type"
            value={counterpartyType}
            onChange={(e) => {
              setCounterpartyType(e.target.value);
              // A selection from one type's registry is meaningless in
              // another's — clear it on type change.
              setSelected(null);
            }}
            className="w-full border border-border-light rounded-sm px-space-2 py-space-1 text-small"
          >
            {COUNTERPARTY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        {searchable ? (
          <CounterpartyPicker
            counterpartyType={counterpartyType}
            value={selected}
            onChange={setSelected}
          />
        ) : (
          <div>
            <Label htmlFor="counterparty-id">Counterparty ID (UUID)</Label>
            <Input
              id="counterparty-id"
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              data-testid="counterparty-id-input"
            />
            <p className="text-caption text-gray-mid mt-1 m-0">
              {counterpartyType} records have no searchable registry yet —
              paste the record's UUID.
            </p>
          </div>
        )}
        <div>
          <Label htmlFor="role-on-loan">Role on loan</Label>
          <Input
            id="role-on-loan"
            value={roleOnLoan}
            onChange={(e) => setRoleOnLoan(e.target.value)}
            placeholder="e.g. Borrower, Guarantor, Broker"
          />
        </div>
        <div>
          <Label htmlFor="party-effective-from">Effective from</Label>
          <Input
            id="party-effective-from"
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            checked={isKeyPerson}
            onChange={(e) => setIsKeyPerson(e.target.checked)}
          />
          Key person
        </label>
        <label className="flex items-center gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            checked={isPrimaryContact}
            onChange={(e) => setIsPrimaryContact(e.target.checked)}
          />
          Primary contact (one per loan)
        </label>
        <div>
          <Label htmlFor="party-notes">Notes</Label>
          <Textarea
            id="party-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
        </div>
        <div className="flex items-center justify-end gap-space-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={add.isPending || !counterpartyId.trim() || !roleOnLoan.trim()}
            data-testid="add-party-submit"
          >
            {add.isPending ? "Adding…" : "Add party"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
